import React, { useEffect, useMemo, useState } from "react";
import { Container, Table, Button, Alert, Card, Spinner, Form, Row, Col, Badge, Modal } from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import API from "../services/api";
import "./SalesMetaLeads.css";
import "./AdminMetaLeads.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";

const STAGES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "interested", label: "Interested" },
  { value: "not_interested", label: "Not Interested" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const stageLabel = (stage) => {
  const match = STAGES.find((s) => s.value === stage);
  return match ? match.label : stage || "New";
};

const formatFieldLabel = (key) => {
  const raw = String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!raw) return "Question";
  return raw
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const extractFormFields = (lead) => {
  const raw = lead?.raw;
  const fieldData = raw?.field_data;
  if (!Array.isArray(fieldData)) return [];
  return fieldData
    .filter((f) => f && f.name)
    .map((f) => {
      const key = String(f.name);
      const value = Array.isArray(f.values) ? f.values.join(", ") : f.values;
      return { key, value: value || "—" };
    });
};

const buildDeepReportNote = (draft = {}) => {
  const lines = [
    "Deep Report",
    `Interaction Type: ${draft.interactionType || "—"}`,
    `Lead Temperature: ${draft.leadTemperature || "—"}`,
    `Budget: ${draft.budget || "—"}`,
    `Timeline: ${draft.timeline || "—"}`,
    `Decision Maker: ${draft.decisionMaker || "—"}`,
    `Current Vendor: ${draft.currentVendor || "—"}`,
    `Pain Point: ${draft.painPoint || "—"}`,
    `Competitor: ${draft.competitor || "—"}`,
    `Next Action: ${draft.nextAction || "—"}`,
    `Detailed Note: ${draft.note || "—"}`,
  ];
  return lines.join("\n");
};

const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const DEEP_REPORT_LABELS = [
  "Interaction Type",
  "Lead Temperature",
  "Budget",
  "Timeline",
  "Decision Maker",
  "Current Vendor",
  "Pain Point",
  "Competitor",
  "Next Action",
  "Detailed Note",
];

const formatFollowUpNote = (note) => {
  const raw = String(note || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .trim();
  if (!raw) return "—";

  const hasDeepReportFields =
    /deep report/i.test(raw) ||
    DEEP_REPORT_LABELS.some((label) =>
      new RegExp(`${escapeRegExp(label)}\\s*:`, "i").test(raw)
    );

  if (!hasDeepReportFields) return raw;

  let normalized = raw;
  if (!normalized.includes("\n")) {
    const labelsPattern = DEEP_REPORT_LABELS.map(escapeRegExp).join("|");
    normalized = normalized
      .replace(new RegExp(`\\s*(${labelsPattern})\\s*:`, "gi"), "\n$1:")
      .trim();
  }

  normalized = normalized.replace(/^Deep Report\s*/i, "").trim();
  normalized = normalized.replace(/\n{3,}/g, "\n\n");
  return normalized || "—";
};

const compactFollowUpNote = (note) => formatFollowUpNote(note).replace(/\n+/g, " | ");

const formatAssignedNames = (assignedTo) => {
  if (!assignedTo) return "Unassigned";
  const list = Array.isArray(assignedTo) ? assignedTo : [assignedTo];
  const names = list.map((a) => a?.fullName || a?.mobile || a?.email).filter(Boolean);
  return names.length ? names.join(", ") : "Unassigned";
};

const initials = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return "LD";
  const parts = raw.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "LD";
};

const SalesMetaLeads = () => {
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [manualLead, setManualLead] = useState({
    name: "",
    phone: "",
    email: "",
    source: "Meta Manual",
    campaignName: "",
    campaignId: "",
    category: "",
    leadSource: "",
    product: "",
    notes: "",
    status: "new",
    nextFollowUpAt: "",
  });
  const [manualSaving, setManualSaving] = useState(false);
  const [followUpDrafts, setFollowUpDrafts] = useState({});
  const [statusSaving, setStatusSaving] = useState({});
  const [followUpSaving, setFollowUpSaving] = useState({});
  const [editDrafts, setEditDrafts] = useState({});
  const [editSaving, setEditSaving] = useState({});
  const [detailLead, setDetailLead] = useState(null);
  const [detailLeadKey, setDetailLeadKey] = useState("");
  const [deepReportDrafts, setDeepReportDrafts] = useState({});
  const [deepReportSaving, setDeepReportSaving] = useState(false);

  const openLeadWhatsApp = async (lead) => {
    const phone = lead?.phone || lead?.mobile || "";
    if (!phone) {
      setErr("Lead phone/WhatsApp number not available");
      return;
    }
    try {
      await sendPortalWhatsApp({
        to: phone,
        recipientName: lead?.name || lead?.fullName || "Lead",
        message: `Hello ${lead?.name || lead?.fullName || "Lead"}, thank you for your interest.`,
        sourceModule: "meta_leads_sales",
        eventType: "manual_lead_message",
        entityType: "MetaLead",
        entityId: lead?._id || lead?.leadId || "",
        isAdmin: false,
      });
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get("/employee/sales/meta-leads");
      const rows = Array.isArray(res.data?.leads)
        ? res.data.leads
        : Array.isArray(res.data)
        ? res.data
        : [];
      setLeads(rows);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load Meta leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const leadIdentity = (lead) =>
    String(lead?._id || lead?.leadId || lead?.id || "");

  const updateLeadLocal = (lead) => {
    const targetId = leadIdentity(lead);
    if (!targetId) return;
    setLeads((prev) =>
      prev.map((l) => (leadIdentity(l) === targetId ? lead : l))
    );
  };

  const createManual = async () => {
    try {
      setManualSaving(true);
      setErr("");
      const payload = {
        name: manualLead.name,
        phone: manualLead.phone,
        email: manualLead.email,
        source: manualLead.source || "Meta Manual",
        campaignName: manualLead.campaignName,
        campaignId: manualLead.campaignId,
        category: manualLead.category,
        leadSource: manualLead.leadSource,
        product: manualLead.product,
        notes: manualLead.notes,
        status: manualLead.status,
        nextFollowUpAt: manualLead.nextFollowUpAt || undefined,
      };
      const res = await API.post("/employee/sales/meta-leads", payload);
      if (res.data?.lead) {
        setLeads((prev) => [res.data.lead, ...prev]);
      } else {
        await fetchLeads();
      }
      setManualLead({
        name: "",
        phone: "",
        email: "",
        source: "Meta Manual",
        campaignName: "",
        campaignId: "",
        category: "",
        leadSource: "",
        product: "",
        notes: "",
        status: "new",
        nextFollowUpAt: "",
      });
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to add manual lead");
    } finally {
      setManualSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      setStatusSaving((prev) => ({ ...prev, [id]: true }));
      const res = await API.patch(`/employee/sales/meta-leads/${id}/status`, { status });
      if (res.data?.lead) {
        updateLeadLocal(res.data.lead);
        if (detailLead && (detailLead._id === id || detailLead.leadId === id)) {
          setDetailLead(res.data.lead);
        }
      }
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to update status");
    } finally {
      setStatusSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  const saveFollowUp = async (id) => {
    const draft = followUpDrafts[id] || {};
    if (!draft.note) {
      setErr("Follow-up note is required");
      return;
    }
    try {
      setFollowUpSaving((prev) => ({ ...prev, [id]: true }));
      const res = await API.post(`/employee/sales/meta-leads/${id}/followups`, {
        note: draft.note,
        status: draft.stage || undefined,
        nextFollowUpAt: draft.nextFollowUpAt || undefined,
        followUpAt: new Date().toISOString(),
      });
      if (res.data?.lead) updateLeadLocal(res.data.lead);
      setFollowUpDrafts((prev) => ({
        ...prev,
        [id]: { note: "", stage: "", nextFollowUpAt: "" },
      }));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to save follow-up");
    } finally {
      setFollowUpSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  const getLeadKey = (lead, idx) => lead?._id || lead?.leadId || idx;

  const initDraftFor = (lead) => ({
    category: lead?.category || "",
    leadSource: lead?.leadSource || "",
    product: lead?.product || "",
  });

  const updateDraftField = (key, lead, field, value) => {
    setEditDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || initDraftFor(lead)),
        [field]: value,
      },
    }));
  };

  const saveLeadEdits = async (lead, idx) => {
    const key = getLeadKey(lead, idx);
    const draft = editDrafts[key] || initDraftFor(lead);
    try {
      setEditSaving((prev) => ({ ...prev, [key]: true }));
      setErr("");
      const res = await API.put(`/employee/sales/meta-leads/${lead._id || lead.leadId}`, {
        category: draft.category,
        leadSource: draft.leadSource,
        product: draft.product,
      });
      if (res.data?.lead) {
        updateLeadLocal(res.data.lead);
        setEditDrafts((prev) => ({ ...prev, [key]: initDraftFor(res.data.lead) }));
        if (detailLead && (detailLead._id === res.data.lead._id || detailLead.leadId === res.data.lead.leadId)) {
          setDetailLead(res.data.lead);
        }
      }
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to update lead");
    } finally {
      setEditSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const getDeepReportDraft = (key, lead) => {
    if (deepReportDrafts[key]) return deepReportDrafts[key];
    return {
      interactionType: "",
      leadTemperature: "",
      budget: "",
      timeline: "",
      decisionMaker: "",
      currentVendor: "",
      painPoint: "",
      competitor: "",
      nextAction: "",
      note: "",
      stage: lead?.status || "new",
      nextFollowUpAt: "",
    };
  };

  const openLeadDetail = (lead, idx) => {
    const key = getLeadKey(lead, idx);
    setDetailLead(lead);
    setDetailLeadKey(key);
    setDeepReportDrafts((prev) => ({
      ...prev,
      [key]: getDeepReportDraft(key, lead),
    }));
  };

  const closeLeadDetail = () => {
    setDetailLead(null);
    setDetailLeadKey("");
  };

  const updateDeepReportField = (field, value) => {
    if (!detailLeadKey) return;
    setDeepReportDrafts((prev) => ({
      ...prev,
      [detailLeadKey]: {
        ...(prev[detailLeadKey] || getDeepReportDraft(detailLeadKey, detailLead)),
        [field]: value,
      },
    }));
  };

  const saveDeepReport = async () => {
    if (!detailLead || !detailLeadKey) return;
    const draft = deepReportDrafts[detailLeadKey] || getDeepReportDraft(detailLeadKey, detailLead);
    if (!String(draft.note || "").trim()) {
      setErr("Deep report note is required");
      return;
    }
    try {
      setErr("");
      setDeepReportSaving(true);
      const leadId = detailLead?._id || detailLead?.leadId || detailLead?.id;
      if (!leadId) {
        setErr("Lead ID not found. Please refresh and try again.");
        return;
      }
      const res = await API.post(`/employee/sales/meta-leads/${leadId}/followups`, {
        note: buildDeepReportNote(draft),
        status: draft.stage || undefined,
        nextFollowUpAt: draft.nextFollowUpAt || undefined,
        followUpAt: new Date().toISOString(),
      });
      if (res.data?.lead) {
        updateLeadLocal(res.data.lead);
        setDetailLead(res.data.lead);
        setDeepReportDrafts((prev) => ({
          ...prev,
          [detailLeadKey]: getDeepReportDraft(detailLeadKey, res.data.lead),
        }));
      }
      await fetchLeads();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to submit deep report");
    } finally {
      setDeepReportSaving(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const filteredLeads = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return leads;
    return (Array.isArray(leads) ? leads : []).filter((l) => {
      const haystack = [
        l?.name,
        l?.fullName,
        l?.phone,
        l?.mobile,
        l?.email,
        l?.category,
        l?.leadSource,
        l?.product,
        l?.status,
        stageLabel(l?.status),
        l?.source,
        l?.platform,
        l?.campaign,
        l?.campaignName,
        l?.campaignId,
        l?.formId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, searchQuery]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");
  const fmtLeadDatePill = (d) => {
    if (!d) return "No Date";
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return "No Date";
    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Container className="mt-4 meta-leads-page meta-leads-shell">
      <div className="meta-leads-hero">
        <div className="meta-leads-hero-left">
          <div className="meta-leads-hero-title">
            <div className="meta-leads-hero-icon">ML</div>
            <div>
              <h2 className="mb-0">Meta Leads</h2>
              <div className="meta-leads-hero-sub">
                <span className="meta-leads-live-dot" />
                <span>Live Workspace</span>
              </div>
            </div>
          </div>
          <div className="meta-leads-hero-stats">
            {filteredLeads.length}
            {searchQuery.trim() ? ` / ${leads.length}` : ""} Leads Found
          </div>
        </div>
        <div className="meta-leads-hero-right">
          <div className="meta-leads-hero-search">
            <span className="meta-leads-hero-search-icon">⌕</span>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, phone, campaign, status..."
            />
          </div>
          <div className="meta-leads-hero-actions">
            <Button variant="outline-secondary" size="sm" onClick={fetchLeads}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <section className="meta-leads-section">
        <div className="meta-leads-section-head">
          <div className="meta-leads-section-title">
            <h5 className="mb-0">Manual Lead Entry</h5>
          </div>
          <div className="meta-leads-section-meta">
            Add leads manually and start follow-ups.
          </div>
        </div>
        <div className="meta-leads-manual-card">
          <div className="meta-leads-config-head">
            <div>
              <h6 className="mb-1">Manual Meta Lead</h6>
              <div className="meta-leads-config-sub">Add Meta Ads leads manually and start follow-ups.</div>
            </div>
          </div>
          <Form className="meta-leads-config-form">
          <Row>
            <Col md={4} className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={manualLead.name}
                onChange={(e) => setManualLead((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Lead Name"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={manualLead.phone}
                onChange={(e) => setManualLead((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={manualLead.email}
                onChange={(e) => setManualLead((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
              />
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={manualLead.status}
                onChange={(e) => setManualLead((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Control
                value={manualLead.category}
                onChange={(e) => setManualLead((prev) => ({ ...prev, category: e.target.value }))}
                placeholder="Category"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Product</Form.Label>
              <Form.Control
                value={manualLead.product}
                onChange={(e) => setManualLead((prev) => ({ ...prev, product: e.target.value }))}
                placeholder="Product / Service"
              />
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-3">
              <Form.Label>Campaign Name</Form.Label>
              <Form.Control
                value={manualLead.campaignName}
                onChange={(e) =>
                  setManualLead((prev) => ({ ...prev, campaignName: e.target.value }))
                }
                placeholder="Campaign Name"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Lead Source</Form.Label>
              <Form.Control
                value={manualLead.leadSource}
                onChange={(e) => setManualLead((prev) => ({ ...prev, leadSource: e.target.value }))}
                placeholder="Lead source"
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label>Campaign ID</Form.Label>
              <Form.Control
                value={manualLead.campaignId}
                onChange={(e) => setManualLead((prev) => ({ ...prev, campaignId: e.target.value }))}
                placeholder="Campaign ID"
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Next Follow-up</Form.Label>
              <Form.Control
                type="datetime-local"
                value={manualLead.nextFollowUpAt}
                onChange={(e) =>
                  setManualLead((prev) => ({ ...prev, nextFollowUpAt: e.target.value }))
                }
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={manualLead.notes}
                onChange={(e) => setManualLead((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes"
              />
            </Col>
          </Row>
            <div className="meta-leads-config-actions">
              <Button variant="primary" onClick={createManual} disabled={manualSaving}>
                {manualSaving ? "Saving..." : "Add Lead"}
              </Button>
            </div>
          </Form>
        </div>
      </section>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <section className="meta-leads-section">
            <div className="meta-leads-section-head">
              <div className="meta-leads-section-title">
                <h5 className="mb-0">Leads Database</h5>
              </div>
              <div className="meta-leads-section-actions">
                <button className="meta-leads-ghost-btn" type="button">
                  Filter
                </button>
                <button className="meta-leads-ghost-btn" type="button">
                  CSV
                </button>
              </div>
            </div>
            <div className="meta-leads-table-wrap">
              <div className="meta-leads-table-card">
              <Table hover responsive className="meta-leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Category</th>
                    <th>Lead Source</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Next Follow-up</th>
                    <th>Source</th>
                    <th>Campaign</th>
                    <th>Campaign ID</th>
                    <th>Form ID</th>
                    <th>Submitted</th>
                    <th>Notes</th>
                    <th>Follow-up</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="text-center">
                        No leads found
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((l, i) => (
                      <tr key={l._id || l.id || i}>
                        <td>
                          <div className="meta-lead-name">{l.name || l.fullName || "—"}</div>
                        </td>
                        <td>
                          <div className="meta-lead-contact">
                            <span>{l.phone || l.mobile || "—"}</span>
                            <span>{l.email || "—"}</span>
                            <Button size="sm" variant="outline-success" onClick={() => openLeadWhatsApp(l)}>
                              <span className="d-inline-flex align-items-center gap-1">
                                <BsWhatsapp />
                                <span>WhatsApp</span>
                              </span>
                            </Button>
                          </div>
                        </td>
                        <td>{l.category || "—"}</td>
                        <td>{l.leadSource || "—"}</td>
                        <td>{l.product || "—"}</td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={l.status || "new"}
                            onChange={(e) => updateStatus(l._id || l.leadId, e.target.value)}
                            disabled={!!statusSaving[l._id || l.leadId]}
                          >
                            {STAGES.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td className="meta-lead-date">{fmtDate(l.nextFollowUpAt)}</td>
                        <td>{l.source || l.platform || "Meta"}</td>
                        <td>{l.campaign || l.campaignName || "—"}</td>
                        <td>{l.campaignId || "—"}</td>
                        <td>{l.formId || "—"}</td>
                        <td className="meta-lead-date">{fmtDate(l.createdAt || l.submittedAt)}</td>
                        <td>{l.notes || "—"}</td>
                        <td>
                          {(() => {
                            const leadKey = l._id || l.leadId || i;
                            const draft = followUpDrafts[leadKey] || {};
                            const last = Array.isArray(l.followUps) && l.followUps.length
                              ? l.followUps[l.followUps.length - 1]
                              : null;
                            return (
                              <div className="meta-lead-followup">
                                <Form.Control
                                  size="sm"
                                  placeholder="Follow-up note"
                                  value={draft.note || ""}
                                  onChange={(e) =>
                                    setFollowUpDrafts((prev) => ({
                                      ...prev,
                                      [leadKey]: { ...draft, note: e.target.value },
                                    }))
                                  }
                                />
                                <Form.Select
                                  size="sm"
                                  value={draft.stage || ""}
                                  onChange={(e) =>
                                    setFollowUpDrafts((prev) => ({
                                      ...prev,
                                      [leadKey]: { ...draft, stage: e.target.value },
                                    }))
                                  }
                                >
                                  <option value="">Stage (optional)</option>
                                  {STAGES.map((s) => (
                                    <option key={s.value} value={s.value}>
                                      {s.label}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control
                                  size="sm"
                                  type="datetime-local"
                                  value={draft.nextFollowUpAt || ""}
                                  onChange={(e) =>
                                    setFollowUpDrafts((prev) => ({
                                      ...prev,
                                      [leadKey]: { ...draft, nextFollowUpAt: e.target.value },
                                    }))
                                  }
                                />
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => saveFollowUp(l._id || l.leadId)}
                                  disabled={!!followUpSaving[l._id || l.leadId]}
                                >
                                  {followUpSaving[l._id || l.leadId] ? "Saving..." : "Add"}
                                </Button>
                                {last ? (
                                  <div className="meta-lead-last">
                                    Last: {stageLabel(last.status)} • {compactFollowUpNote(last.note)} • {fmtDate(last.createdAt)}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          <div className="meta-leads-cards">
            {filteredLeads.length === 0 ? (
              <div className="text-center">No leads found</div>
            ) : (
              filteredLeads.map((l, i) => (
                <Card key={l._id || l.id || i} className="meta-lead-card">
                  <Card.Body>
                    <div className="meta-lead-top">
                      <div className="meta-lead-name">{l.name || l.fullName || "—"}</div>
                      <span className="meta-lead-date-pill">
                        {fmtLeadDatePill(l.createdAt || l.submittedAt)}
                      </span>
                    </div>
                    <div className="meta-lead-contact">
                      <span>{l.phone || l.mobile || "—"}</span>
                      <span>{l.email || "—"}</span>
                    </div>
                    <div className="meta-lead-row">
                      <span className="label">Status</span>
                      <span className="value">
                        <Badge bg="info">{stageLabel(l.status)}</Badge>
                      </span>
                    </div>
                    <div className="meta-lead-row">
                      <span className="label">Campaign</span>
                      <span className="value">{l.campaign || l.campaignName || "—"}</span>
                    </div>
                    <div className="meta-lead-row">
                      <span className="label">Next Follow-up</span>
                      <span className="value">{fmtDate(l.nextFollowUpAt)}</span>
                    </div>
                    <div className="meta-lead-actions">
                      <Button size="sm" variant="outline-primary" onClick={() => openLeadDetail(l, i)}>
                        View Details
                      </Button>
                      <Button size="sm" variant="outline-success" onClick={() => openLeadWhatsApp(l)}>
                        <span className="d-inline-flex align-items-center gap-1">
                          <BsWhatsapp />
                          <span>WhatsApp</span>
                        </span>
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>
          </section>
        </>
      )}

      <Modal show={!!detailLead} onHide={closeLeadDetail} size="xl" centered dialogClassName="meta-lead-detail-modal">
        <Modal.Header closeButton className="mlm-header">
          <div className="mlm-header-left">
            <div className="mlm-header-title">Lead Details & Deep Report</div>
            <Badge bg="primary" className="mlm-header-badge">
              {stageLabel(detailLead?.status)}
            </Badge>
          </div>
        </Modal.Header>
        <Modal.Body className="mlm-body">
          {detailLead ? (
            <>
              <div className="mlm-summary">
                <div className="mlm-summary-lead">
                  <div className="mlm-avatar">{initials(detailLead.name || detailLead.fullName)}</div>
                  <div>
                    <div className="mlm-kicker">Lead Name</div>
                    <div className="mlm-lead-name">{detailLead.name || detailLead.fullName || "—"}</div>
                  </div>
                </div>
                <div className="mlm-summary-grid">
                  <div><span>Phone</span><b>{detailLead.phone || detailLead.mobile || "—"}</b></div>
                  <div><span>Email</span><b>{detailLead.email || "—"}</b></div>
                  <div><span>Executive</span><b>{formatAssignedNames(detailLead.assignedTo)}</b></div>
                  <div><span>Submitted</span><b>{fmtDate(detailLead.createdAt || detailLead.submittedAt)}</b></div>
                  <div>
                    <span>WhatsApp</span>
                    <b>
                      <Button size="sm" variant="outline-success" onClick={() => openLeadWhatsApp(detailLead)}>
                        <span className="d-inline-flex align-items-center gap-1">
                          <BsWhatsapp />
                          <span>Open Chat</span>
                        </span>
                      </Button>
                    </b>
                  </div>
                </div>
              </div>

              {(() => {
                const leadKey = detailLeadKey || getLeadKey(detailLead, 0);
                const draft = editDrafts[leadKey] || initDraftFor(detailLead);
                const deepDraft = deepReportDrafts[leadKey] || getDeepReportDraft(leadKey, detailLead);
                const fields = extractFormFields(detailLead);
                const history = Array.isArray(detailLead.followUps) ? detailLead.followUps : [];
                return (
                  <div className="mlm-grid">
                    <div className="mlm-main-col">
                      <section className="mlm-card">
                        <div className="mlm-card-head">
                          <h6>Lead Information</h6>
                          <small>Last sync: {fmtDate(detailLead.updatedAt || detailLead.createdAt)}</small>
                        </div>
                        <div className="mlm-card-body">
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Label>Category</Form.Label>
                              <Form.Control
                                value={draft.category}
                                onChange={(e) => updateDraftField(leadKey, detailLead, "category", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Lead Source</Form.Label>
                              <Form.Control
                                value={draft.leadSource}
                                onChange={(e) => updateDraftField(leadKey, detailLead, "leadSource", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Product</Form.Label>
                              <Form.Control
                                value={draft.product}
                                onChange={(e) => updateDraftField(leadKey, detailLead, "product", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                value={detailLead.status || "new"}
                                onChange={(e) => updateStatus(detailLead._id || detailLead.leadId, e.target.value)}
                                disabled={!!statusSaving[detailLead._id || detailLead.leadId]}
                              >
                                {STAGES.map((s) => (
                                  <option key={s.value} value={s.value}>
                                    {s.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col md={6} className="d-flex align-items-end">
                              <Button
                                variant="outline-secondary"
                                onClick={async () => {
                                  await saveLeadEdits(detailLead, 0);
                                  await fetchLeads();
                                }}
                                disabled={!!editSaving[leadKey]}
                              >
                                {editSaving[leadKey] ? "Saving..." : "Save Lead Info"}
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      </section>

                      <section className="mlm-card">
                        <div className="mlm-card-head">
                          <h6>Deep Qualification Report</h6>
                        </div>
                        <div className="mlm-card-body">
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Label>Interaction Type</Form.Label>
                              <Form.Control
                                placeholder="Call / Visit / WhatsApp"
                                value={deepDraft.interactionType || ""}
                                onChange={(e) => updateDeepReportField("interactionType", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Lead Temperature</Form.Label>
                              <Form.Select
                                value={deepDraft.leadTemperature || ""}
                                onChange={(e) => updateDeepReportField("leadTemperature", e.target.value)}
                              >
                                <option value="">Select</option>
                                <option value="Hot">Hot</option>
                                <option value="Warm">Warm</option>
                                <option value="Cold">Cold</option>
                              </Form.Select>
                            </Col>
                            <Col md={4}>
                              <Form.Label>Next Follow-up</Form.Label>
                              <Form.Control
                                type="datetime-local"
                                value={deepDraft.nextFollowUpAt || ""}
                                onChange={(e) => updateDeepReportField("nextFollowUpAt", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Budget</Form.Label>
                              <Form.Control
                                value={deepDraft.budget || ""}
                                onChange={(e) => updateDeepReportField("budget", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Timeline</Form.Label>
                              <Form.Control
                                value={deepDraft.timeline || ""}
                                onChange={(e) => updateDeepReportField("timeline", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Decision Maker</Form.Label>
                              <Form.Control
                                value={deepDraft.decisionMaker || ""}
                                onChange={(e) => updateDeepReportField("decisionMaker", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Current Vendor</Form.Label>
                              <Form.Control
                                value={deepDraft.currentVendor || ""}
                                onChange={(e) => updateDeepReportField("currentVendor", e.target.value)}
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Label>Competitor</Form.Label>
                              <Form.Control
                                value={deepDraft.competitor || ""}
                                onChange={(e) => updateDeepReportField("competitor", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Pain Point</Form.Label>
                              <Form.Control
                                value={deepDraft.painPoint || ""}
                                onChange={(e) => updateDeepReportField("painPoint", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Next Action</Form.Label>
                              <Form.Control
                                value={deepDraft.nextAction || ""}
                                onChange={(e) => updateDeepReportField("nextAction", e.target.value)}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Update Stage</Form.Label>
                              <Form.Select
                                value={deepDraft.stage || ""}
                                onChange={(e) => updateDeepReportField("stage", e.target.value)}
                              >
                                {STAGES.map((s) => (
                                  <option key={s.value} value={s.value}>
                                    {s.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col md={12}>
                              <Form.Label>Detailed Note</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={5}
                                placeholder="Write a complete conversation summary..."
                                value={deepDraft.note || ""}
                                onChange={(e) => updateDeepReportField("note", e.target.value)}
                              />
                            </Col>
                          </Row>
                          <div className="mlm-submit-wrap">
                            <Button onClick={saveDeepReport} disabled={deepReportSaving}>
                              {deepReportSaving ? "Submitting..." : "Submit Deep Report"}
                            </Button>
                          </div>
                        </div>
                      </section>
                    </div>

                    <div className="mlm-side-col">
                      <section className="mlm-card mlm-side-card">
                        <div className="mlm-card-head">
                          <h6>Follow-up History</h6>
                        </div>
                        <div className="mlm-card-body mlm-timeline">
                          {history.length ? (
                            history
                              .slice()
                              .reverse()
                              .map((f, idx) => (
                                <div key={`${f.createdAt || idx}`} className="mlm-timeline-item">
                                  <div className="mlm-timeline-dot" />
                                  <div className="mlm-timeline-content">
                                    <div className="mlm-timeline-time">{fmtDate(f.createdAt)}</div>
                                    <div className="mlm-timeline-stage">{stageLabel(f.status)}</div>
                                    <div className="mlm-timeline-text">{formatFollowUpNote(f.note)}</div>
                                  </div>
                                </div>
                              ))
                          ) : (
                            <div className="text-muted">No follow-up yet</div>
                          )}
                        </div>
                      </section>

                      {fields.length ? (
                        <section className="mlm-card">
                          <div className="mlm-card-head">
                            <h6>Raw Form Data</h6>
                          </div>
                          <div className="mlm-card-body">
                            <div className="mlm-raw">
                              <div className="mlm-raw-head">
                                <span>Question</span>
                                <span>Answer</span>
                              </div>
                              {fields.map((f, idx) => (
                                <div key={`${f.key}-${idx}`} className="mlm-raw-row">
                                  <span className="mlm-raw-q">{formatFieldLabel(f.key)}</span>
                                  <b className="mlm-raw-a">{String(f.value || "—")}</b>
                                </div>
                              ))}
                            </div>
                          </div>
                        </section>
                      ) : null}

                      <section className="mlm-insights">
                        <h6>Lead Insights</h6>
                        <div>Campaign: {detailLead.campaign || detailLead.campaignName || "—"}</div>
                        <div>Form ID: {detailLead.formId || "—"}</div>
                        <div>Source: {detailLead.source || detailLead.platform || "Meta"}</div>
                      </section>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default SalesMetaLeads;
